/** The fields of an account's form (name, cloud settings, key), shared by Connect and the drawer. */
import { SECRET_MIN_LENGTH, cleanLaunchAccountSettings, launchAccountAgent, launchAccountSecretNeed } from '@orgus/protocol';
import type { LaunchAccount, LaunchAccountAgent, LaunchAccountKind, LaunchAccountSettings } from '@orgus/protocol';
import { Input } from '@orgus/ui';

export interface Draft {
  name: string;
  agent: LaunchAccountAgent;
  kind: LaunchAccountKind;
  resource: string;
  region: string;
  profile: string;
  projectId: string;
  secret: string;
}

export const emptyDraft = (agent: LaunchAccountAgent = 'claude-code', kind: LaunchAccountKind = 'subscription'): Draft => ({
  name: '',
  agent,
  kind,
  resource: '',
  region: '',
  profile: '',
  projectId: '',
  secret: '',
});

export function draftOf(a: LaunchAccount): Draft {
  const s = a.settings;
  return {
    name: a.name,
    agent: launchAccountAgent(a),
    kind: a.kind,
    resource: s.resource ?? s.baseUrl ?? '',
    region: s.region ?? '',
    profile: s.profile ?? '',
    projectId: s.projectId ?? '',
    secret: '',
  };
}

export function settingsOf(d: Draft): LaunchAccountSettings {
  const r = d.resource.trim();
  return {
    ...(r.startsWith('https://') ? { baseUrl: r } : r ? { resource: r } : {}),
    ...(d.region.trim() ? { region: d.region.trim() } : {}),
    ...(d.profile.trim() ? { profile: d.profile.trim() } : {}),
    ...(d.projectId.trim() ? { projectId: d.projectId.trim() } : {}),
  };
}

export function KindFields({ d, set }: { d: Draft; set: (p: Partial<Draft>) => void }) {
  if (d.kind === 'foundry') return <Input label="Resource" value={d.resource} onChange={(e) => set({ resource: e.target.value })} placeholder="my-resource" hint="The resource name alone, or its full https base URL." spellCheck={false} className="font-mono" />;
  if (d.kind === 'bedrock')
    return (
      <div className="grid gap-3 sm:grid-cols-2">
        <Input label="Region" value={d.region} onChange={(e) => set({ region: e.target.value })} placeholder="us-east-1" hint="Optional: else the profile's region." spellCheck={false} className="font-mono" />
        <Input label="AWS profile" value={d.profile} onChange={(e) => set({ profile: e.target.value })} placeholder="default" hint="Optional: a profile in ~/.aws on the machine." spellCheck={false} className="font-mono" />
      </div>
    );
  if (d.kind === 'vertex') {
    const gemini = d.agent === 'gemini-cli';
    return (
      <div className="grid gap-3 sm:grid-cols-2">
        <Input label="Project id" value={d.projectId} onChange={(e) => set({ projectId: e.target.value })} placeholder="acme-ai" spellCheck={false} className="font-mono" />
        <Input
          label={gemini ? 'Location' : 'Region'}
          value={d.region}
          onChange={(e) => set({ region: e.target.value })}
          placeholder={gemini ? 'us-central1' : 'global'}
          hint={gemini ? undefined : 'Optional: global, us, eu or a region.'}
          spellCheck={false}
          className="font-mono"
        />
      </div>
    );
  }
  return null;
}

export const KEY_SOURCE: Record<LaunchAccountAgent, string> = {
  'claude-code': 'the Anthropic Console',
  codex: 'the OpenAI Platform',
  'gemini-cli': 'Google AI Studio',
};

/** A form error in a sentence, before anything is sent. */
export function draftError(d: Draft, editing: LaunchAccount | null): string | null {
  const s = cleanLaunchAccountSettings(d.kind, settingsOf(d), d.agent);
  if (s.error) return s.error;
  const need = launchAccountSecretNeed(d.kind, d.agent);
  if (need === 'required' && !editing?.secret && !d.secret.trim()) return `Paste the API key from ${KEY_SOURCE[d.agent]}.`;
  if (d.secret && d.secret.trim().length < SECRET_MIN_LENGTH) return `A key is at least ${SECRET_MIN_LENGTH} characters.`;
  return null;
}
